import { normalizeName } from './normalize';

/** Obtiene un valor por ruta con puntos y corchetes: "data.fields[0].value", "field_data.phone_number". */
export function getPath(payload: unknown, path: string): unknown {
  if (!path) return undefined;
  const parts = path
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);
  let cur: unknown = payload;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    if (Array.isArray(cur) && /^\d+$/.test(p)) cur = cur[Number(p)];
    else if (typeof cur === 'object') cur = (cur as Record<string, unknown>)[p];
    else return undefined;
  }
  return cur;
}

/** Campos del lead que se pueden mapear desde un payload externo. */
export const MAPPABLE_FIELDS = [
  'firstName',
  'lastNamePaternal',
  'lastNameMaternal',
  'fullName',
  'phone',
  'email',
  'documentType',
  'documentNumber',
  'district',
  'projectRef',
  'advisorRef',
  'medium',
  'entryChannel',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent',
  'note',
  'externalId',
  'capturedAt',
  'campaignId',
  'adsetId',
  'adId',
  'formId',
  'sourceUrl',
] as const;
export type MappableField = (typeof MAPPABLE_FIELDS)[number];

export const MAPPABLE_FIELD_LABELS: Record<MappableField, string> = {
  firstName: 'Nombres',
  lastNamePaternal: 'Apellido paterno',
  lastNameMaternal: 'Apellido materno',
  fullName: 'Nombre completo (se separa)',
  phone: 'Celular',
  email: 'Correo',
  documentType: 'Tipo de documento',
  documentNumber: 'Número de documento',
  district: 'Distrito',
  projectRef: 'Proyecto (id, nombre o código externo)',
  advisorRef: 'Asesor (usuario, correo o nombre)',
  medium: 'Medio de captación',
  entryChannel: 'Canal de entrada',
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  note: 'Nota',
  externalId: 'ID externo',
  capturedAt: 'Fecha de captación',
  campaignId: 'ID de campaña',
  adsetId: 'ID de conjunto de anuncios',
  adId: 'ID de anuncio',
  formId: 'ID de formulario',
  sourceUrl: 'URL de origen',
};

/** Destino de un mapeo: un campo del lead o un campo personalizado de la empresa (`custom.<clave>`). */
export type MappingTarget = MappableField | `custom.${string}`;

export interface FieldMapping {
  /** Ruta dentro del payload (o nombre de columna CSV). */
  fieldMap: Partial<Record<MappingTarget, string>>;
  /** Valores fijos cuando el payload no trae el campo. */
  defaults: Partial<Record<MappingTarget, string>>;
}

export interface MappedLead {
  firstName: string | null;
  lastNamePaternal: string | null;
  lastNameMaternal: string | null;
  phone: string | null;
  email: string | null;
  documentType: string | null;
  documentNumber: string | null;
  district: string | null;
  projectRef: string | null;
  advisorRef: string | null;
  medium: string | null;
  entryChannel: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  note: string | null;
  externalId: string | null;
  capturedAt: string | null;
  campaignId: string | null;
  adsetId: string | null;
  adId: string | null;
  formId: string | null;
  sourceUrl: string | null;
  /** Campos personalizados (`custom.<clave>` en el mapeo), aún como texto: se validan al crear el lead. */
  custom: Record<string, string>;
}

function asText(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return asText(v[0]);
  if (typeof v === 'object') return null;
  const s = String(v).trim();
  return s ? s : null;
}

/** "Juan Carlos Pérez Gómez" → nombres + apellidos (heurística: los dos últimos tokens son apellidos). */
export function splitFullName(full: string): {
  firstName: string;
  lastNamePaternal: string | null;
  lastNameMaternal: string | null;
} {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1)
    return { firstName: parts[0] ?? '', lastNamePaternal: null, lastNameMaternal: null };
  if (parts.length === 2)
    return { firstName: parts[0]!, lastNamePaternal: parts[1]!, lastNameMaternal: null };
  return {
    firstName: parts.slice(0, -2).join(' '),
    lastNamePaternal: parts[parts.length - 2]!,
    lastNameMaternal: parts[parts.length - 1]!,
  };
}

/** Aplica el mapeo a un payload: rutas primero, luego valores por defecto. Nunca lanza. */
export function applyMapping(payload: unknown, mapping: FieldMapping): MappedLead {
  const get = (f: MappableField): string | null => {
    const path = mapping.fieldMap[f];
    const v = path ? asText(getPath(payload, path)) : null;
    return v ?? mapping.defaults[f] ?? null;
  };
  const out: MappedLead = {
    firstName: get('firstName'),
    lastNamePaternal: get('lastNamePaternal'),
    lastNameMaternal: get('lastNameMaternal'),
    phone: get('phone'),
    email: get('email'),
    documentType: get('documentType'),
    documentNumber: get('documentNumber'),
    district: get('district'),
    projectRef: get('projectRef'),
    advisorRef: get('advisorRef'),
    medium: get('medium'),
    entryChannel: get('entryChannel'),
    utmSource: get('utmSource'),
    utmMedium: get('utmMedium'),
    utmCampaign: get('utmCampaign'),
    utmTerm: get('utmTerm'),
    utmContent: get('utmContent'),
    note: get('note'),
    externalId: get('externalId'),
    capturedAt: get('capturedAt'),
    campaignId: get('campaignId'),
    adsetId: get('adsetId'),
    adId: get('adId'),
    formId: get('formId'),
    sourceUrl: get('sourceUrl'),
    custom: {},
  };
  const full = get('fullName');
  if (!out.firstName && full) Object.assign(out, splitFullName(full));
  const customKeys = new Set(
    [...Object.keys(mapping.fieldMap), ...Object.keys(mapping.defaults)].filter((k) =>
      k.startsWith('custom.'),
    ),
  );
  for (const k of customKeys) {
    const key = k as MappingTarget;
    const path = mapping.fieldMap[key];
    const v = (path ? asText(getPath(payload, path)) : null) ?? mapping.defaults[key] ?? null;
    if (v !== null) out.custom[k.slice('custom.'.length)] = v;
  }
  return out;
}

/** Sugerencia automática de mapeo a partir de los nombres de columna / claves del payload. */
export function suggestMapping(keys: readonly string[]): Partial<Record<MappableField, string>> {
  const rules: Array<[MappableField, RegExp]> = [
    ['phone', /^(tel|cel|phone|whatsapp|movil|celular|telefono|phone_number)/],
    ['email', /^(mail|correo|e_?mail)/],
    ['fullName', /^(nombre_completo|full_?name|nombre_y_apellido|cliente|contacto)$/],
    ['firstName', /^(nombre|nombres|first_?name)$/],
    ['lastNamePaternal', /^(apellido|apellidos|apellido_paterno|last_?name|ap_?paterno)$/],
    ['lastNameMaternal', /^(apellido_materno|ap_?materno)$/],
    ['documentNumber', /^(dni|documento|doc|nro_?doc|num_?doc)/],
    ['district', /^(distrito|district)/],
    ['projectRef', /^(proyecto|project|obra)/],
    ['advisorRef', /^(asesor|advisor|vendedor|ejecutivo|responsable|owner)/],
    ['medium', /^(medio|source|fuente|origen)/],
    ['entryChannel', /^(canal|channel)/],
    ['utmSource', /^utm_?source$/],
    ['utmMedium', /^utm_?medium$/],
    ['utmCampaign', /^(utm_?campaign|campa[ñn]a)$/],
    ['utmTerm', /^utm_?term$/],
    ['utmContent', /^utm_?content$/],
    ['note', /^(nota|notas|comentario|mensaje|message|observaciones)/],
    ['externalId', /^(id|codigo|external_?id|codprospecto|lead_?id)$/],
    ['capturedAt', /^(fecha|fecha_?registro|created|date|captured)/],
    ['campaignId', /^(campaign_?id|id_?campa[ñn]a)$/],
    ['adsetId', /^(adset_?id|ad_?group_?id)$/],
    ['adId', /^(ad_?id|id_?anuncio)$/],
    ['formId', /^(form_?id|id_?formulario)$/],
    ['sourceUrl', /^(url|source_?url|page_?url|landing)/],
  ];
  const out: Partial<Record<MappableField, string>> = {};
  for (const key of keys) {
    const k = normalizeName(key).toLowerCase().replace(/\s+/g, '_');
    for (const [field, re] of rules) {
      if (!out[field] && re.test(k)) {
        out[field] = key;
        break;
      }
    }
  }
  return out;
}

/** Hash canónico de un payload (claves ordenadas) para idempotencia. Devuelve la cadena a hashear. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`)
    .join(',')}}`;
}

/** Registros de un CSV tal como vienen (sin cabecera interpretada), con su número de fila. */
export interface CsvRecords {
  /** Registros con algún dato, en orden, como listas de celdas sin recortar. */
  records: string[][];
  /**
   * Número de fila de cada registro en el archivo, como lo muestra Excel (la primera es la 1): las filas en
   * blanco cuentan aunque no se devuelvan, así «fila 7» en un error es la fila 7 del archivo de la persona.
   */
  rowNumbers: number[];
  delimiter: string;
}

/**
 * Separador de columnas: «;» o «,», el que más veces aparezca en la primera línea que traiga alguno (un título
 * sin separadores arriba de la cabecera no decide). Empate o ninguno → «;» (formato de Excel en español).
 */
function detectDelimiter(clean: string): string {
  const lines = clean.split(/\r?\n/, 10);
  const line = lines.find((l) => /[;,]/.test(l)) ?? '';
  return (line.match(/;/g)?.length ?? 0) >= (line.match(/,/g)?.length ?? 0) ? ';' : ',';
}

/** Lector de CSV posicional (comillas, «;» o «,», saltos de línea dentro de comillas); omite las filas en blanco. */
export function parseCsvRecords(text: string): CsvRecords {
  const clean = text.replace(/^\uFEFF/, '');
  const delimiter = detectDelimiter(clean);
  const records: string[][] = [];
  const rowNumbers: number[] = [];
  let rowNumber = 1;
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const endRecord = () => {
    row.push(field);
    field = '';
    if (row.some((v) => v.trim() !== '')) {
      records.push(row);
      rowNumbers.push(rowNumber);
    }
    rowNumber += 1;
    row = [];
  };
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (clean[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === delimiter) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && clean[i + 1] === '\n') i++;
      endRecord();
    } else field += c;
  }
  if (field !== '' || row.length) endRecord();
  return { records, rowNumbers, delimiter };
}

/** CSV con cabecera en la primera fila con datos: filas como objetos cabecera → valor recortado. */
export function parseCsv(text: string): {
  headers: string[];
  rows: Record<string, string>[];
  delimiter: string;
} {
  const { records, delimiter } = parseCsvRecords(text);
  const headers = (records[0] ?? []).map((h) => h.trim());
  const rows = records
    .slice(1)
    .map((r) => Object.fromEntries(headers.map((h, i) => [h, (r[i] ?? '').trim()])));
  return { headers, rows, delimiter };
}
